/**
 * Persistent extraction state management
 * Tracks knowledge extraction progress across page refreshes
 */

const STORAGE_KEY = 'mazo_extraction_state';

export interface ExtractionState {
  isExtracting: boolean;
  startTime: number;
  sourceIds: string[];
}

/**
 * Get the current extraction state from localStorage
 */
export function getExtractionState(): ExtractionState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const state: ExtractionState = JSON.parse(stored);

    // Validate the state structure
    if (
      typeof state.isExtracting !== 'boolean' ||
      typeof state.startTime !== 'number' ||
      !Array.isArray(state.sourceIds)
    ) {
      // Invalid state, clear it
      clearExtractionState();
      return null;
    }

    // If extraction started more than 10 minutes ago, consider it stale
    const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
    if (state.startTime < tenMinutesAgo) {
      clearExtractionState();
      return null;
    }

    return state;
  } catch (err) {
    console.error('Failed to read extraction state:', err);
    return null;
  }
}

/**
 * Save extraction state to localStorage
 */
export function setExtractionState(state: ExtractionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error('Failed to save extraction state:', err);
  }
}

/**
 * Start extraction - mark as in progress
 */
export function startExtraction(sourceIds: string[]): void {
  setExtractionState({
    isExtracting: true,
    startTime: Date.now(),
    sourceIds,
  });
}

/**
 * Stop extraction - mark as complete
 */
export function stopExtraction(): void {
  clearExtractionState();
}

/**
 * Clear extraction state from localStorage
 */
export function clearExtractionState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.error('Failed to clear extraction state:', err);
  }
}

/**
 * Check if extraction is currently in progress
 */
export function isExtractionInProgress(): boolean {
  const state = getExtractionState();
  return state?.isExtracting ?? false;
}
